/**
 * Session adapter — binds a robot-state instance to a teleop-session object.
 *
 * The state module does not know the transport package's internals; it only needs a
 * minimal structural surface (`SessionLike` below) — a plain `{phase, status}` store to
 * subscribe to, plus a couple of discrete event streams. This adapter reacts:
 *
 *   - `incoming_message`                → ingest into robotConfig/robotTelemetry/linkQuality/
 *                                          controlStatus;
 *   - `phase` STARTING                  → reset robot-config AND operator speed-limits for a
 *                                          fresh room (RECONNECTING recovers the SAME room's
 *                                          media layer, so neither is touched there — the
 *                                          config/limits already on hand are still valid);
 *   - `status` NO_VIDEO_PRODUCER        → collapse a still-pending robot-config to error too;
 *   - `robot_info_requested`            → schedule robot-info request via robotConfig (owns retry);
 *   - `robot_info_stopped`              → cancel the robot-info scheduling.
 *
 * Called ONCE per session object's whole lifetime (teleop-session's `createTeleopSession()`
 * returns a long-lived, restartable object — see its module doc comment) — this adapter
 * never needs to be re-wired across a `stop()`/`start()` cycle, only across the host
 * creating a brand new session object (a new page/panel mount).
 *
 * Only the pure mount→slot lookup is injected into the session (see `resolveMountSlot`);
 * all reset/scheduling business logic lives here, in the state layer.
 */

import { slotFromMount } from "./camera-catalog";
import type { RobotState } from "./robot-state";
import { SessionPhase, STATUS_CODES } from "../teleop-session";
import type { SessionPhaseValue, StatusCodeValue } from "../teleop-session";

/** Minimal structural surface of a teleop-session object the adapter subscribes to. */
export interface SessionLike {
  subscribe(
    listener: (
      state: { phase: SessionPhaseValue; status: StatusCodeValue | null },
      previous: { phase: SessionPhaseValue; status: StatusCodeValue | null }
    ) => void
  ): () => void;
  on(
    event: "incoming_message" | "robot_info_requested" | "robot_info_stopped",
    fn: (payload: unknown) => void
  ): () => void;
}

/**
 * Wire a robot-state instance to a session facade.
 * Returns an unsubscribe that detaches ingestion AND cancels capabilities scheduling.
 */
/**
 * Hard ceiling on the whole robot-config stage, anchored to session STARTING/
 * RECONNECTING rather than to the capabilities-request trigger itself — the trigger
 * (the operator's chat DataChannel opening) can fail for reasons upstream of
 * scheduleCapabilitiesRequest ever running (e.g. a signaling-timeout on produceData),
 * in which case its own retry budget never even starts. Generous enough to sit past
 * that budget's own ~38s (20 attempts × 2s) when it DOES run, so it only ever fires
 * first in the "never even requested" case.
 */
const ROBOT_CONFIG_TIMEOUT_MS = 45_000;

export function attachSession(control: RobotState, session: SessionLike): () => void {
  let cancelCapabilities: (() => void) | null = null;
  let robotConfigTimeoutId: ReturnType<typeof setTimeout> | null = null;

  const clearRobotConfigTimeout = () => {
    if (robotConfigTimeoutId !== null) {
      clearTimeout(robotConfigTimeoutId);
      robotConfigTimeoutId = null;
    }
  };

  const offIngest = session.on("incoming_message", (message) => {
    control.robotConfig.ingest(message);
    control.robotTelemetry.ingest(message);
    control.linkQuality.ingest(message);
    control.controlStatus.ingest(message);
    control.armState.ingest(message);
    control.cameraVideoStats.ingest(message);
  });

  // One plain subscription, diffing phase/status itself — no selector middleware:
  // teleop-session's `phase`/`status`/`connectionQuality` fields are already written
  // through separate `setState` calls at the source (see its own module), so a listener
  // here is never invoked for an unrelated connectionQuality tick in the first place.
  const offState = session.subscribe((state, previous) => {
    if (state.phase !== previous.phase) {
      // STARTING only — NOT RECONNECTING. RECONNECTING means a media-layer recovery of
      // the SAME room (see teleop-session's own CONNECT_DONE_PHASES doc comment: it only
      // ever occurs after an initial successful connect), so the robot-config already on
      // hand (camera catalog, limits) is still valid and must not be thrown away — doing
      // so used to flip `robot.ready` false for the duration of every single reconnect,
      // which dropped the whole interface (video grid, drive controls) into a "waiting for
      // robot-config" placeholder even though nothing about the robot's config changed.
      // `ingest()`'s own guard (skip a fresh "robot-config" message once already "ready")
      // means a rejoin's re-requested capabilities response is simply a no-op here, same
      // as if this reset had run — the only difference is nothing goes stale in between.
      if (state.phase === SessionPhase.STARTING) {
        control.robotConfig.reset();
        control.controlStatus.reset();
        control.armState.reset();
        control.cameraVideoStats.reset();
        clearRobotConfigTimeout();
        robotConfigTimeoutId = setTimeout(() => {
          robotConfigTimeoutId = null;
          control.robotConfig.timeout(`нет robot-config за ${ROBOT_CONFIG_TIMEOUT_MS / 1000}s`);
        }, ROBOT_CONFIG_TIMEOUT_MS);
        control.speedLimits.reset();
      }
    }
    // An empty/producer-less room fails robot-config too, not just video — without
    // this, the "robot" step could sit spinning for up to its own ~45s ceiling while
    // the "ready" step already shows a video error, which reads as broken rather than
    // two steps failing for the same reason. No-op (see robotConfig.timeout()) once
    // status has already resolved one way or another, and still reversible — a genuine
    // robot-config response arriving afterward flips it back to "ready" as normal.
    if (state.status !== previous.status && state.status === STATUS_CODES.NO_VIDEO_PRODUCER) {
      control.robotConfig.timeout("нет ответа robot-config: похоже, комната пуста");
    }
  });

  const offCapsRequested = session.on("robot_info_requested", (payload) => {
    cancelCapabilities?.();
    const send =
      payload && typeof payload === "object" && "send" in payload
        ? (payload as { send: (text: string) => boolean | void }).send
        : () => false;
    cancelCapabilities = control.robotConfig.scheduleCapabilitiesRequest(send);
    control.cameraVideoStats.startClockSync(send);
  });

  const offCapsStopped = session.on("robot_info_stopped", () => {
    control.cameraVideoStats.stopClockSync();
    cancelCapabilities?.();
    cancelCapabilities = null;
  });

  return () => {
    control.cameraVideoStats.stopClockSync();
    cancelCapabilities?.();
    cancelCapabilities = null;
    clearRobotConfigTimeout();
    offIngest();
    offState();
    offCapsRequested();
    offCapsStopped();
  };
}

/**
 * Build the mount→slot resolver for `createTeleopSession` from a robot-state instance.
 * This is the ONLY injection point the transport still needs (a synchronous lookup
 * used during video-consumer setup).
 */
export function resolveMountSlot(control: RobotState): (mount: string) => number | null {
  return (mount) => slotFromMount(control.robotConfig.getCameras(), mount);
}
